import { useState, useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './useAuth';

// Base64 Audio samples para evitar carga de assets
export const NOTIFICATION_SOUNDS = {
  beep: "data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU...", // We will use a proper short base64 or just rely on a standard HTML5 beep if possible. Actually, providing long base64s is messy. We can use AudioContext to generate sounds synthetically!
};

export const generateSound = (type = 'pop') => {
  try {
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioCtx.destination);

    if (type === 'beep') {
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(440, audioCtx.currentTime);
      gainNode.gain.setValueAtTime(1, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.1);
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 0.1);
    } else if (type === 'chime') {
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, audioCtx.currentTime);
      gainNode.gain.setValueAtTime(1, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.5);
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 0.5);
    } else if (type === 'crystal') {
      oscillator.type = 'triangle';
      oscillator.frequency.setValueAtTime(1200, audioCtx.currentTime);
      gainNode.gain.setValueAtTime(0.5, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.3);
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 0.3);
    } else if (type === 'alert') {
      oscillator.type = 'square';
      oscillator.frequency.setValueAtTime(300, audioCtx.currentTime);
      gainNode.gain.setValueAtTime(0.2, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.2);
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 0.2);
    } else { // pop
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(800, audioCtx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(100, audioCtx.currentTime + 0.05);
      gainNode.gain.setValueAtTime(1, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.05);
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 0.05);
    }
  } catch (e) {
    console.warn("AudioContext no soportado", e);
  }
};

export const updateFaviconBadge = () => {
    const faviconUrl = '/favicon-32x32.png?v=3';
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
    }
    link.href = faviconUrl;
};

export const useNotifications = () => {
    const { currentUser } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const prevCountRef = useRef(0);
    
    const role = currentUser?.role?.toUpperCase() || 'OPERATIVO';
    const isAdminOrGerente = role === 'ADMIN' || role === 'GERENTE';
    const isSupervisor = role === 'SUPERVISOR';
    const canSeeAudits = isAdminOrGerente || isSupervisor;
    const isMarketing = role.includes('MARKETING');

    // Track previous stock to detect additions for marketing
    const prevStockRef = useRef({});

    useEffect(() => {
        if (!currentUser) return;

        let bitacoraUnsub = () => {};
        let stockUnsub = () => {};
        let configUnsub = () => {};

        // We fetch config to know reorder points
        let reorderPoints = {};
        const qConfig = query(collection(db, 'config_servicios'));
        configUnsub = onSnapshot(qConfig, (snap) => {
            const data = {};
            snap.forEach(doc => {
                data[doc.data().nombre] = doc.data().punto_de_reorden || 5;
            });
            reorderPoints = data;
        });

        const updateAllNotifications = (bitacoraDocs, stockDocs) => {
            const notifs = [];
            
            // Bitacora (solo si puede auditar)
            if (canSeeAudits) {
                bitacoraDocs.forEach(doc => {
                    const data = doc.data();
                    notifs.push({
                        id: `bitacora_${doc.id}`,
                        type: 'verificacion',
                        title: 'Actividad Pendiente de Verificación',
                        message: `La plataforma ${data.plataforma} registró una actividad: ${data.accion}`,
                        link: '/inventario',
                        timestamp: data.fecha?.toMillis() || Date.now()
                    });
                });
            }

            // Stock bajo (todos)
            stockDocs.forEach(doc => {
                const data = doc.data();
                const pr = reorderPoints[data.plataforma] || 5;
                if (data.unidades < pr) {
                    notifs.push({
                        id: `stock_${doc.id}`,
                        type: 'alerta_stock',
                        title: 'Stock Bajo',
                        message: `${data.plataforma} tiene solo ${data.unidades} unidades (Mínimo: ${pr})`,
                        link: '/inventario',
                        timestamp: Date.now() // Computed stat
                    });
                }

                // Marketing: Check if stock increased
                if (isMarketing) {
                    const prevUnidades = prevStockRef.current[doc.id] || 0;
                    if (data.unidades > prevUnidades && prevUnidades > 0) { // Only alert if it actually increased and it's not the initial load
                        notifs.push({
                            id: `mkt_stock_${doc.id}_${Date.now()}`,
                            type: 'marketing_alerta',
                            title: '¡Stock Añadido!',
                            message: `Se han añadido ${data.unidades - prevUnidades} unid. de ${data.plataforma}. ¡Anúncialo!`,
                            link: '/',
                            timestamp: Date.now()
                        });
                    }
                    prevStockRef.current[doc.id] = data.unidades;
                }
            });

            notifs.sort((a,b) => b.timestamp - a.timestamp);
            setNotifications(notifs);
            setUnreadCount(notifs.length);
        };

        let currentBitacora = [];
        let currentStock = [];

        // Listen Bitacora (solo si es admin/super)
        if (canSeeAudits) {
            const qBit = query(collection(db, 'inventario_bitacora'), where('verificado', '==', false));
            bitacoraUnsub = onSnapshot(qBit, (snap) => {
                currentBitacora = snap.docs;
                updateAllNotifications(currentBitacora, currentStock);
            });
        }

        // Listen Stock
        const qStock = query(collection(db, 'almacen_stock'));
        stockUnsub = onSnapshot(qStock, (snap) => {
            currentStock = snap.docs;
            updateAllNotifications(currentBitacora, currentStock);
        });

        return () => {
            bitacoraUnsub();
            stockUnsub();
            configUnsub();
        };
    }, [currentUser, canSeeAudits, isMarketing]);

    useEffect(() => {
        // Trigger sounds and favicon if count increases
        updateFaviconBadge(unreadCount);
        
        if (unreadCount > prevCountRef.current) {
            // Check user preferences
            const soundEnabled = currentUser?.preferences?.soundEnabled !== false;
            const soundType = currentUser?.preferences?.soundType || 'pop';
            
            if (soundEnabled) {
                generateSound(soundType);
            }
        }
        
        prevCountRef.current = unreadCount;
    }, [unreadCount, currentUser]);

    return { notifications, unreadCount };
};
